<!--
  - SPDX-FileCopyrightText: 2026 Conduction B.V.
  - SPDX-License-Identifier: EUPL-1.2
-->

<template>
	<div class="cn-filter-rows">
		<label class="cn-filter-rows__label">{{ t('nextcloud-vue', 'Filter') }}</label>
		<div
			v-for="(row, i) in rows"
			:key="i"
			class="cn-filter-rows__row">
			<CnFieldPicker
				:value="row.key"
				:label="t('nextcloud-vue', 'Property')"
				:options="fields"
				placeholder="status"
				@update="update(i, 'key', $event)" />
			<NcSelect
				:modelValue="row.op"
				:options="opIds"
				:inputLabel="t('nextcloud-vue', 'Operator')"
				:clearable="false"
				@update:modelValue="update(i, 'op', $event)">
				<template #option="{ label: opId }">
					{{ opLabel(opId) }}
				</template>
				<template #selected-option="{ label: opId }">
					{{ opLabel(opId) }}
				</template>
			</NcSelect>
			<NcTextField
				:modelValue="row.value"
				:label="t('nextcloud-vue', 'Value')"
				placeholder="won"
				@update:modelValue="update(i, 'value', $event)" />
			<NcButton
				variant="tertiary"
				:aria-label="t('nextcloud-vue', 'Remove filter')"
				@click="remove(i)">
				<template #icon>
					<Close :size="18" />
				</template>
			</NcButton>
		</div>
		<NcButton variant="tertiary" @click="add">
			<template #icon>
				<Plus :size="18" />
			</template>
			{{ t('nextcloud-vue', 'Add filter') }}
		</NcButton>
	</div>
</template>

<script>
import { translate as t } from '@nextcloud/l10n'
import { NcButton, NcSelect, NcTextField } from '@nextcloud/vue'
import Close from 'vue-material-design-icons/Close.vue'
import Plus from 'vue-material-design-icons/Plus.vue'
import CnFieldPicker from '../CnFieldPicker/CnFieldPicker.vue'
import { FILTER_OPERATORS } from './filterRows.js'

/**
 * CnFilterRowsEditor — a reusable operator-aware filter editor shared by the
 * widget config forms (stat / chart / object-list). Renders one row per filter
 * condition (`property` + operator `= ≠ > < ≥ ≤` + `value`) with add / remove,
 * and emits the full rows array on every change via `input` (v-model friendly).
 * Use `rowsToFilter` / `filterToRows` from `./filterRows.js` to convert to and
 * from the OpenRegister filter object.
 */
export default {
	name: 'CnFilterRowsEditor',

	components: { NcTextField, NcSelect, NcButton, Plus, Close, CnFieldPicker },

	props: {
		/**
		 * The current filter rows (`{ key, op, value }[]`).
		 *
		 * @type {Array<{key: string, op: string, value: string}>}
		 */
		value: {
			type: Array,
			default: () => [],
		},

		/**
		 * The same value as `value`, under Vue 3's own v-model name.
		 *
		 * ⚠️ WITHOUT THIS, `v-model` ON THIS COMPONENT DOES NOTHING. Vue 3
		 * compiles `v-model="x"` to `:modelValue` + `@update:modelValue`, so a
		 * component declaring only `value`/`input` never receives the prop and
		 * its emit is never heard — silently, looking exactly like a component
		 * that works.
		 *
		 * `value` stays the public name; both are accepted. The default is
		 * `undefined` so "not passed" is distinguishable from "passed empty".
		 *
		 * @type {Array}
		 */
		modelValue: { type: Array, default: undefined },
		/**
		 * Available schema field names — when non-empty the Property input
		 * becomes a dropdown instead of free-text.
		 *
		 * @type {string[]}
		 */
		fields: {
			type: Array,
			default: () => [],
		},
	},

	emits: [
		/**
		 * Emitted with the full updated rows array on every edit.
		 *
		 * @event input
		 * @type {Array<{key: string, op: string, value: string}>}
		 */
		'input',
		/** The same payload under Vue 3's v-model name; see the `modelValue` prop. */
		'update:modelValue',
	],

	data() {
		return {
			rows: (this.modelValue !== undefined ? this.modelValue : this.value).map((r) => ({ ...r })),
		}
	},

	computed: {
		/**
		 * The value the consumer actually bound, whichever prop they used.
		 *
		 * @return {Array} The bound value.
		 */
		boundValue() {
			return this.modelValue !== undefined ? this.modelValue : this.value
		},

		/** Operator ids for the select. */
		opIds() {
			return FILTER_OPERATORS.map((o) => o.id)
		},
	},

	methods: {
		/**
		 * Tell the consumer the value changed, in both v-model dialects.
		 *
		 * BOTH are emitted, always: a consumer on `@input` and a consumer on
		 * `v-model` are the same consumer as far as this component knows, and
		 * emitting only one silently breaks half of them.
		 *
		 * @param {Array} next The new value.
		 * @return {void}
		 */
		emitValue(next) {
			/**
			 * @event input The value changed. Vue 2's v-model dialect, kept for
			 *   existing consumers.
			 * @type {Array}
			 */
			this.$emit('input', next)
			/**
			 * @event update:modelValue The value changed. Vue 3's v-model
			 *   dialect — what a plain `v-model` listens for.
			 * @type {Array}
			 */
			this.$emit('update:modelValue', next)
		},

		t,

		/**
		 * Human label (`=`, `≠`, …) for an operator id.
		 *
		 * @param {string} id An operator id from `FILTER_OPERATORS` (e.g. `'eq'`, `'gt'`).
		 * @return {string} The operator's display label, or `id` itself when unknown.
		 */
		opLabel(id) {
			const op = FILTER_OPERATORS.find((o) => o.id === id)
			return op ? op.label : id
		},

		/** Append a blank equality row. */
		add() {
			this.rows.push({ key: '', op: 'eq', value: '' })
			this.emit()
		},

		/**
		 * Remove a row by index.
		 *
		 * @param {number} i Zero-based index of the row in `rows`.
		 * @return {void}
		 */
		remove(i) {
			this.rows.splice(i, 1)
			this.emit()
		},

		/**
		 * Update one cell of a row and emit.
		 *
		 * @param {number} i Zero-based index of the row in `rows`.
		 * @param {'key'|'op'|'value'} cell Which cell of the row to write.
		 * @param {string} value The new cell value — a property name, an operator
		 *   id from `opIds`, or the free-text comparison value.
		 * @return {void}
		 */
		update(i, cell, value) {
			this.rows[i][cell] = value
			this.emit()
		},

		/** Emit the current rows. */
		emit() {
			this.emitValue(this.rows.map((r) => ({ ...r })))
		},
	},
}
</script>

<style scoped>
.cn-filter-rows {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.cn-filter-rows__label {
	font-size: 0.85em;
	color: var(--color-text-maxcontrast);
}

.cn-filter-rows__row {
	display: flex;
	gap: 8px;
	align-items: flex-end;
}

.cn-filter-rows__row > *:first-child {
	flex: 2;
}

.cn-filter-rows__row > *:nth-child(2) {
	flex: 1;
	min-width: 90px;
}

.cn-filter-rows__row > *:nth-child(3) {
	flex: 2;
}

.cn-filter-rows__row :deep(.button-vue) {
	flex: 0 0 auto;
}
</style>
